import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getActionables, markAsRead, updateActionableReadStatus } from '../../Store/Actionables/action';
import { isToday, isNext60Days } from '../../Utility/calculations';
import { useDispatch } from 'react-redux';
import { requestHideLoader, requestShowLoader } from '../../Store/Global/action';


const DATA_CONFIG = {
    task: {
        idKey: 'taskId'
    },
    lead: {
        idKey: 'leadId'
    }
};


export const useActionablesData = ({
    type,
    selectedOwner,
    ownerlist,
    myTeam,
    prospectStatuses,
    isActive = true
}) => {
    const [actionables, setActionables] = useState([]);
    const [oldActionables, setOldActionables] = useState([]);
    const [filters, setFilters] = useState({
        checked: false, 
        unRead: false
    });

    const config = DATA_CONFIG[type];
    const requestCountRef = useRef(0);
    const dispatch = useDispatch();
    const fetchActionables = useCallback(async () => {
        if (!selectedOwner || !isActive) return;

        const currentRequestId = ++requestCountRef.current;
        dispatch(requestShowLoader())

        try {
            const response = await getActionables(type, selectedOwner, ownerlist, myTeam);


            setActionables(response?.data || []);
            setOldActionables(response?.data || []);

        } catch (error) {
            console.error(`Error fetching ${type} actionables:`, error);
        }
        finally{
            dispatch(requestHideLoader())
        }


    }, [type, selectedOwner, ownerlist, myTeam, isActive]);

    useEffect(() => {
        if (selectedOwner && isActive) {
            fetchActionables();
        }
    }, [selectedOwner, isActive, fetchActionables]);

    useEffect(() => {
        let data = [...oldActionables];

        if (filters?.checked && prospectStatuses.length > 0) {
            data = data.filter(item => prospectStatuses.includes(item?.leadStatus));
        }

        if (filters?.unRead) {
            data = data.filter(item => item?.read === false);
        }

        setActionables(data);
    }, [filters, oldActionables, prospectStatuses]);

    const todayTableData = useMemo(
        () => actionables.filter(item => isToday(item.nextFollowupDate)),
        [actionables]
    );

    const nextDaysTableData = useMemo(
        () => actionables.filter(item => isNext60Days(item.nextFollowupDate)),
        [actionables]
    );

    const markAsReadApi = useCallback(async (row) => {
        setOldActionables(prev => updateActionableReadStatus(prev, row, config.idKey));
        return markAsRead(type, row);
    }, [type, config.idKey]);

    const handleMarkAsRead = useCallback(async (row) => {
        try {
            await markAsReadApi(row);
        } catch (err) {
            console.error('Error marking as read:', err);
        }
    }, [markAsReadApi]);


    const handleMarkAsReadIfUnread = useCallback(async (row) => {
        if (row?.read === false) {
            await handleMarkAsRead(row);
        }
    }, [handleMarkAsRead]);

    const handleToggleProspects = useCallback((event) => {
        setFilters(prev => ({
            ...prev,
            checked: event?.target?.checked
        }));
    }, []);

    const handleToggleUnread = useCallback((event) => {
        setFilters(prev => ({
            ...prev,
            unRead: event?.target?.checked
        }));
    }, []);

   const getRowClassName = (params) => {
  return params.row.read ? 'read-row' : 'unread-row';
};

    return {
        actionables,
        oldActionables,
        todayTableData,
        nextDaysTableData,
        filters,
        setFilters,
        handleMarkAsRead,
        handleMarkAsReadIfUnread,
        markAsReadApi,
        handleToggleProspects,
        handleToggleUnread,
        getRowClassName,
        refetch: fetchActionables,
        idKey: config.idKey
    };
};

export default useActionablesData;
